import moment from 'moment';
import { useTheme } from '@mui/material/styles';

import { MISC_DATE_ISO_DATE_ONLY_FORMAT } from '../../../core/constants';

// brand orange shades, checked with the dataviz palette validator (lightness band, colour-blind and normal-vision separation)
// for each color mode; the shade below 3:1 against the surface relies on legends and tooltips
const CHART_PALETTE = {
    // softer than the brand #FE5000, large filled areas in the full brand orange are tiring
    light: { primary: '#F4823A', secondary: '#9A3412' },
    dark: { primary: '#E8600F', secondary: '#9E4614' }
};

export const useChartColors = () => CHART_PALETTE[useTheme().palette.mode];

export const SalesChannelEnum = {
    POD: { value: 'POD', label: 'Sprzedaż stacjonarna' },
    RESERVATION: { value: 'RESERVATION', label: 'Rezerwacje' }
};

export const GranularityEnum = {
    DAY: { value: 'DAY', label: 'Dni' },
    WEEK: { value: 'WEEK', label: 'Tygodnie' },
    MONTH: { value: 'MONTH', label: 'Miesiące' }
};

export const DateRangePresetEnum = {
    LAST_7_DAYS: {
        value: 'LAST_7_DAYS',
        label: '7 dni',
        range: () => ({ from: moment().subtract(6, 'days'), to: moment() })
    },
    LAST_30_DAYS: {
        value: 'LAST_30_DAYS',
        label: '30 dni',
        range: () => ({ from: moment().subtract(29, 'days'), to: moment() })
    },
    THIS_MONTH: {
        value: 'THIS_MONTH',
        label: 'Ten miesiąc',
        range: () => ({ from: moment().startOf('month'), to: moment() })
    },
    LAST_MONTH: {
        value: 'LAST_MONTH',
        label: 'Poprzedni miesiąc',
        range: () => ({ from: moment().subtract(1, 'month').startOf('month'), to: moment().subtract(1, 'month').endOf('month') })
    }
};

const currencyFormatter = new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN', maximumFractionDigits: 0 });
const preciseCurrencyFormatter = new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN' });
const numberFormatter = new Intl.NumberFormat('pl-PL');
const compactFormatter = new Intl.NumberFormat('pl-PL', { notation: 'compact', maximumFractionDigits: 1 });

export const formatCurrency = (value) => value === null || value === undefined ? '-' : currencyFormatter.format(value);
export const formatPreciseCurrency = (value) => value === null || value === undefined ? '-' : preciseCurrencyFormatter.format(value);
export const formatNumber = (value) => value === null || value === undefined ? '-' : numberFormatter.format(value);
// short axis ticks, e.g. "25 tys. zł"
export const formatCompactCurrency = (value) => `${ compactFormatter.format(value) } zł`;
export const formatCompactNumber = (value) => compactFormatter.format(value);

export const truncateLabel = (label, maxLength = 28) => label?.length > maxLength ? label.substring(0, maxLength - 1) + '…' : label;

export const getPodLabel = (pods, podId) => {
    const pod = pods?.find(pod => pod?.id === podId);
    return pod ? `${ pod?.indoorPartner?.name } (${ pod?.description })` : podId;
};

export const buildStatsQueryParams = (filters) => {
    const queryParams = {
        from: filters.from.format(MISC_DATE_ISO_DATE_ONLY_FORMAT),
        to: filters.to.format(MISC_DATE_ISO_DATE_ONLY_FORMAT)
    };

    if (filters.podIds?.length > 0) {
        queryParams.podId = filters.podIds.join(',');
    }

    return queryParams;
};

// percentage change against previous period, null when there is nothing to compare with
export const getChange = (current, previous) => {
    if (!previous || Number(previous) === 0) {
        return null;
    }

    return ((Number(current) - Number(previous)) / Number(previous)) * 100;
};
